import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import BackordersWidget from "@/components/BackordersWidget";
import AdminListNudgesWidget from "@/components/AdminListNudgesWidget";
import DashboardChartsRow from "@/components/DashboardChartsRow";
import StockThumb from "@/components/StockThumb";
import ItemHistoryDialog from "@/components/ItemHistoryDialog";
import { DepartmentBadge } from "@/components/DepartmentChips";
import { fmtQty } from "@/lib/pack";
import {
  AlertTriangle,
  TrendingDown,
  TrendingUp,
  Package as PackageIcon,
  ArrowRight,
  PackagePlus,
  Hammer,
  Hourglass,
  ChevronDown,
  ChevronRight,
  FileText,
  Send,
  Clock,
  Mail,
  Camera,
  Cog,
  Monitor,
  Truck,
  HardHat,
  Building2,
} from "lucide-react";

// Small pill shown under a low-stock row telling the manager whether
// there's already a PO or backorder in flight for this item. Prevents
// double-ordering the same line just because the reorder alert is
// still red. Prefers the "louder" pill: Sent > Draft > Backorder,
// falls back to nothing if there's no active action.
const LowStockActionPill = ({ openPo, openBackorder }) => {
  if (openPo?.status === "sent" || openPo?.status === "partially_received") {
    const dateStr = openPo.sent_at
      ? new Date(openPo.sent_at).toLocaleDateString(undefined, {
          day: "numeric",
          month: "short",
        })
      : "";
    const timeStr = openPo.sent_at
      ? new Date(openPo.sent_at).toLocaleTimeString(undefined, {
          hour: "numeric",
          minute: "2-digit",
        })
      : "";
    return (
      <div
        className="mt-1 inline-flex items-center flex-wrap gap-x-1.5 gap-y-0.5 px-2 py-1 rounded border-2 border-[#1F6B3A] bg-[#E4F0E6] text-[11px] font-bold text-[#1F6B3A] leading-tight"
        data-testid={`low-stock-po-pill-${openPo.id}`}
        title={`PO ${openPo.po_number}${
          openPo.status === "partially_received" ? " · partially received" : ""
        }`}
      >
        <Send size={11} strokeWidth={2.75} />
        <span>
          Sent {dateStr}
          {timeStr ? ` · ${timeStr}` : ""}
        </span>
        {openPo.sent_to_email && (
          <span className="inline-flex items-center gap-0.5 font-medium text-[10px] opacity-90">
            <Mail size={10} strokeWidth={2.75} />
            {openPo.sent_to_email}
          </span>
        )}
        <span className="font-medium text-[10px] opacity-80">
          · {openPo.po_number}
        </span>
      </div>
    );
  }
  if (openPo?.status === "draft") {
    return (
      <div
        className="mt-1 inline-flex items-center gap-1 px-2 py-1 rounded border-2 border-[#B85B1F] bg-[#FFEDD6] text-[11px] font-bold text-[#8A4210] leading-tight"
        data-testid={`low-stock-po-pill-${openPo.id}`}
        title={`${openPo.po_number} · not sent yet`}
      >
        <FileText size={11} strokeWidth={2.75} />
        Draft PO ready · {openPo.po_number}
      </div>
    );
  }
  if (openBackorder) {
    const etaTxt = openBackorder.eta
      ? ` · ETA ${new Date(openBackorder.eta).toLocaleDateString(undefined, {
          day: "numeric",
          month: "short",
        })}`
      : "";
    return (
      <div
        className="mt-1 inline-flex items-center gap-1 px-2 py-1 rounded border-2 border-[#0D1F3C] bg-[#DFEEF7] text-[11px] font-bold text-[#0D1F3C] leading-tight"
        data-testid={`low-stock-bo-pill-${openBackorder.id}`}
      >
        <Clock size={11} strokeWidth={2.75} />
        On backorder{etaTxt}
      </div>
    );
  }
  return null;
};

const Stat = ({ label, value, sub, icon: Icon, tid, accent = false, danger = false, success = false, onClick }) => {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`nb-card p-5 flex flex-col gap-1 min-h-[150px] relative overflow-hidden text-left w-full ${
        onClick ? "transition hover:-translate-y-0.5 hover:shadow-lg cursor-pointer" : ""
      }`}
      data-testid={tid}
      style={{
        background: danger ? "#FFF1F3" : success ? "#E7F7EE" : accent ? "#DFEEF7" : "#FFFFFF",
      }}
    >
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">{label}</div>
        <Icon
          size={22}
          strokeWidth={2.5}
          className={danger ? "text-[#E5263F]" : success ? "text-[#4DA86A]" : accent ? "text-[#228EBF]" : "text-black"}
        />
      </div>
      <div className="font-mono-num font-bold text-6xl leading-none mt-2 tracking-tight">
        {value}
      </div>
      {sub && <div className="label-eyebrow mt-auto pt-2">{sub}</div>}
    </Tag>
  );
};

// Compact SVG progress ring rendered inside PhotoCoverageRings. Keeping
// it inline (rather than pulling in a chart lib) so the Dashboard stays
// lean — this ring never animates or resizes so a plain SVG is enough.
const Ring = ({ percent, colour }) => {
  const r = 22;
  const c = 2 * Math.PI * r;
  const filled = c * Math.min(1, Math.max(0, percent / 100));
  return (
    <svg width={56} height={56} viewBox="0 0 56 56">
      <circle
        cx={28}
        cy={28}
        r={r}
        stroke="#E5E7EB"
        strokeWidth={7}
        fill="none"
      />
      <circle
        cx={28}
        cy={28}
        r={r}
        stroke={colour}
        strokeWidth={7}
        strokeLinecap="round"
        fill="none"
        strokeDasharray={`${filled} ${c}`}
        transform="rotate(-90 28 28)"
      />
      <text
        x={28}
        y={32}
        textAnchor="middle"
        fontSize={13}
        fontWeight={800}
        fill="#0D1F3C"
      >
        {Math.round(percent)}%
      </text>
    </svg>
  );
};

// Three-across photo coverage panel — Inventory / Machinery / Assets.
// Each card is a link straight to the matching Quick Photos tab so the
// manager can jump from "42%" to "which items are missing?" in one tap.
const PhotoCoverageRings = ({ data, nav }) => {
  const KINDS = [
    {
      key: "stock",
      label: "Inventory",
      colour: "#228EBF",
      counts: data.stock,
    },
    {
      key: "machinery",
      label: "Machinery",
      colour: "#7A4A00",
      counts: data.machinery,
    },
    {
      key: "assets",
      label: "Assets",
      colour: "#1F6B3A",
      counts: data.assets,
    },
  ];
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6"
      data-testid="dash-photo-coverage"
    >
      {KINDS.map((k) => {
        const total = k.counts?.total || 0;
        const have = k.counts?.with_photo || 0;
        const pct = total > 0 ? (have / total) * 100 : 0;
        const missing = Math.max(0, total - have);
        return (
          <button
            type="button"
            key={k.key}
            onClick={() =>
              nav(`/quick-photos?kind=${k.key}&missing=1`)
            }
            data-testid={`photo-coverage-${k.key}`}
            className="nb-card p-4 flex items-center gap-4 text-left transition hover:-translate-y-0.5 hover:shadow-lg"
            title={
              total > 0
                ? `${have} of ${total} ${k.label.toLowerCase()} have a photo — tap to fill the gaps`
                : `No ${k.label.toLowerCase()} yet`
            }
          >
            <Ring percent={pct} colour={k.colour} />
            <div className="flex-1 min-w-0">
              <div className="label-eyebrow flex items-center gap-1">
                <Camera size={11} strokeWidth={2.75} /> Photos · {k.label}
              </div>
              <div className="font-display text-2xl leading-tight">
                {have} of {total}
              </div>
              <div className="text-[11px] text-[#607087]">
                {missing > 0
                  ? `${missing} missing — tap to fix`
                  : total > 0
                    ? "All covered — well done"
                    : "Nothing to photograph yet"}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [assetAlerts, setAssetAlerts] = useState({ warrantySoon: [], broken: [] });
  const [machineAlerts, setMachineAlerts] = useState({
    due_soon: 0,
    overdue: 0,
    broken: 0,
    due_soon_items: [],
    overdue_items: [],
    broken_items: [],
  });
  // Lightweight backorders summary — feeds the Backorders stat tile without
  // waiting on the widget's own fetch. The widget still refreshes itself
  // when opened/edited; this just powers the tile count.
  const [backorderSummary, setBackorderSummary] = useState({
    open_count: 0,
    overdue_count: 0,
  });
  const [agreementRenewals, setAgreementRenewals] = useState([]);
  const [photoCoverage, setPhotoCoverage] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  // Section collapse — sections get long as the app grows, so let the user
  // fold what they don't need. Defaults keep the important stuff open.
  const [expBackorders, setExpBackorders] = useState(true);
  const [expLowStock, setExpLowStock] = useState(true);
  const [expRecent, setExpRecent] = useState(true);
  // "Show all N" toggle on Low Stock — when off, we cap at the top 6
  // most urgent items so the panel stays scannable. Zero-stock items
  // are pushed to the front by the backend sort.
  const [showAllLow, setShowAllLow] = useState(false);
  const { user, isManager } = useAuth();
  const nav = useNavigate();

  const fmtWhen = (iso) => {
    if (!iso) return "";
    try {
      const d = new Date(iso);
      const today = new Date();
      const sameDay = d.toDateString() === today.toDateString();
      const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      if (sameDay) return time;
      const date = d.toLocaleDateString([], { day: "numeric", month: "short" });
      return `${date} · ${time}`;
    } catch {
      return "";
    }
  };

  const openItemHistory = async (stockId) => {
    if (!stockId) return;
    try {
      const { data } = await api.get(`/stock/${stockId}`);
      setHistoryFor(data);
    } catch (e) {
      // item may have been deleted — log but don't toast
      console.warn("Could not open item history:", e);
    }
  };

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data));
    api.get("/assets").then(({ data }) => {
      const now = new Date();
      const in90 = new Date(now.getTime() + 90 * 86400000);
      const warrantySoon = (data || []).filter((a) => {
        if (!a.warranty_until) return false;
        const d = new Date(a.warranty_until);
        return d >= now && d <= in90;
      });
      const broken = (data || []).filter((a) =>
        ["broken_unconfirmed", "broken"].includes(a.status),
      );
      setAssetAlerts({ warrantySoon, broken });
    }).catch(() => {});
    api.get("/machines/dashboard/summary").then(({ data }) => {
      setMachineAlerts(data || machineAlerts);
    }).catch(() => {});
    api.get("/backorders/dashboard/summary").then(({ data }) => {
      setBackorderSummary(data || { open_count: 0, overdue_count: 0 });
    }).catch(() => {});
    api.get("/service-agreements/dashboard/renewals").then(({ data }) => {
      setAgreementRenewals(data?.items || []);
    }).catch(() => {});
    if (isManager) {
      api
        .get("/dashboard/photo-coverage")
        .then(({ data }) => setPhotoCoverage(data))
        .catch(() => {});
    }
  }, [isManager]);

  return (
    <div className="p-4 md:p-8 lg:p-10 max-w-[1400px]" data-testid="dashboard-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Today at the lab</div>
          <h1 className="font-display text-5xl md:text-6xl leading-[0.95] mt-2">
            Hi, {user?.name?.split(" ")[0]}.
          </h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isManager && (
            <>
              <button
                data-testid="quick-catalog-btn"
                onClick={() => nav("/catalog")}
                className="nb-btn bg-white px-4 py-3 flex items-center gap-2 text-sm"
              >
                <PackageIcon size={16} strokeWidth={2.5} /> Manage catalog
              </button>
              <button
                data-testid="quick-receive-btn"
                onClick={() => nav("/receive")}
                className="nb-btn nb-btn-dark px-4 py-3 flex items-center gap-2 text-sm"
              >
                <PackagePlus size={16} strokeWidth={2.5} /> Receive stock
              </button>
            </>
          )}
          <button
            data-testid="quick-take-btn"
            onClick={() => nav("/take")}
            className="nb-btn nb-btn-primary px-5 py-4 flex items-center gap-2 text-lg"
          >
            <Hammer size={20} strokeWidth={2.5} />
            Take stock
            <ArrowRight size={18} strokeWidth={2.5} />
          </button>
        </div>
      </div>

      {isManager && <DashboardChartsRow />}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 mt-8">
        <Stat
          tid="stat-total-items"
          label="Inventory Items"
          value={stats?.total_items ?? "—"}
          sub={isManager ? "tap to manage" : "distinct SKUs"}
          icon={PackageIcon}
          onClick={isManager ? () => nav("/catalog") : undefined}
        />
        <Stat
          tid="stat-today-out"
          label="Taken Today"
          value={stats?.today_out_count ?? "—"}
          sub={`${stats?.today_out_qty?.toFixed?.(0) ?? 0} units · tap to view`}
          icon={TrendingDown}
          accent
          onClick={() => nav("/history?tab=OUT")}
        />
        <Stat
          tid="stat-today-in"
          label="Received Today"
          value={stats?.today_in_count ?? "—"}
          sub="tap to receive more"
          icon={TrendingUp}
          success
          onClick={() => nav("/receive")}
        />
        <Stat
          tid="stat-low-stock"
          label="Low Stock"
          value={stats?.low_stock_count ?? "—"}
          sub={
            (stats?.low_stock_count ?? 0) > 0
              ? isManager
                ? "tap to reorder"
                : "tap to view"
              : "below threshold"
          }
          icon={AlertTriangle}
          danger
          onClick={
            (stats?.low_stock_count ?? 0) > 0
              ? () => nav(isManager ? "/orders" : "/take")
              : undefined
          }
        />
        <Stat
          tid="stat-backorders"
          label="Backorders"
          value={backorderSummary.open_count}
          sub={
            backorderSummary.overdue_count > 0
              ? `${backorderSummary.overdue_count} overdue · tap to view`
              : backorderSummary.open_count > 0
                ? "open · tap to view"
                : "none open"
          }
          icon={Hourglass}
          danger={backorderSummary.overdue_count > 0}
          accent={
            backorderSummary.open_count > 0 && backorderSummary.overdue_count === 0
          }
          onClick={() => {
            setExpBackorders(true);
            // Give the panel a tick to expand before scrolling to it.
            setTimeout(() => {
              document
                .querySelector('[data-testid="dash-backorders"]')
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 50);
          }}
        />
      </div>

      {/* Entity counts row — machinery, assets, suppliers, technicians.
          Manager-only; staff already have the Inventory Items tile above
          and don't need the meta counts. Each card taps through to the
          matching page so the Dashboard doubles as a nav launcher. */}
      {isManager && (
        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-5 mt-5"
          data-testid="dash-entity-counts"
        >
          <Stat
            tid="stat-machinery-count"
            label="Machinery"
            value={stats?.machinery_count ?? "—"}
            sub="tap to manage"
            icon={Cog}
            onClick={() => nav("/machinery")}
          />
          <Stat
            tid="stat-assets-count"
            label="Assets"
            value={stats?.assets_count ?? "—"}
            sub="tap to manage"
            icon={Monitor}
            onClick={() => nav("/assets")}
          />
          <Stat
            tid="stat-suppliers-count"
            label="Suppliers"
            value={stats?.suppliers_count ?? "—"}
            sub="tap to manage"
            icon={Truck}
            onClick={() => nav("/suppliers")}
          />
          <Stat
            tid="stat-technicians-count"
            label="Technicians"
            value={stats?.technicians_count ?? "—"}
            sub="tap to manage"
            icon={HardHat}
            onClick={() => nav("/technicians")}
          />
          <Stat
            tid="stat-site-services-count"
            label="Site Services"
            value={stats?.site_services_count ?? "—"}
            sub={
              stats?.site_services_overdue > 0
                ? `${stats.site_services_overdue} overdue`
                : "tap to manage"
            }
            icon={Building2}
            onClick={() => nav("/site-services")}
            danger={stats?.site_services_overdue > 0}
          />
        </div>
      )}

      {/* Photo coverage — three quick progress rings showing how much of
          each entity has at least one photo. Encourages the WM to walk
          around with the iPad and tap "Take" on the Quick Photos page,
          which is where each ring clicks through to. Manager-only. */}
      {isManager && photoCoverage && (
        <PhotoCoverageRings data={photoCoverage} nav={nav} />
      )}

      {/* Service agreement renewals — surfaces contracts within 60 days of
          expiring so managers can budget + negotiate well before the date. */}
      {agreementRenewals.length > 0 && (
        <div
          className="nb-card p-4 mt-6"
          data-testid="dash-agreement-renewals"
          style={{ background: "#FFF9EC", borderColor: "#7A4A00" }}
        >
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <FileText size={16} strokeWidth={2.5} className="text-[#7A4A00]" />
            <h3 className="font-display text-lg">Service agreements renewing</h3>
            <span className="label-eyebrow px-2 py-0.5 rounded-full border border-black/30 bg-white text-[#7A4A00]">
              {agreementRenewals.length}
            </span>
            {isManager && (
              <button
                data-testid="dash-agreements-view-all-btn"
                onClick={() => nav("/service-agreements")}
                className="nb-btn bg-white px-3 py-1 text-xs ml-auto"
              >
                Manage
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {agreementRenewals.slice(0, 6).map((a) => {
              const d = a.expiry_status?.days_remaining;
              const lvl = a.expiry_status?.level;
              const bg =
                lvl === "expired"
                  ? "#E5263F"
                  : lvl === "urgent"
                    ? "#FCC8CE"
                    : lvl === "warn"
                      ? "#FFE1CC"
                      : "#FFF3D4";
              const fg = lvl === "expired" ? "#fff" : "#7A2E00";
              return (
                <button
                  key={a.id}
                  data-testid={`dash-agreement-chip-${a.id}`}
                  onClick={() => nav("/service-agreements")}
                  className="border-2 border-black rounded-lg px-2 py-1 text-xs flex flex-col items-start gap-0.5 bg-white text-left hover:opacity-80"
                  title={a.supplier_name || ""}
                >
                  <span className="font-bold truncate max-w-[220px]">
                    {a.name}
                  </span>
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded font-bold"
                    style={{ background: bg, color: fg }}
                  >
                    {d != null
                      ? d >= 0
                        ? `${d}d left`
                        : `${Math.abs(d)}d overdue`
                      : "no end date"}
                    {a.machines?.length ? ` · ${a.machines.length} machine${a.machines.length === 1 ? "" : "s"}` : ""}
                  </span>
                </button>
              );
            })}
            {agreementRenewals.length > 6 && (
              <div className="text-xs text-[#607087] self-center">
                +{agreementRenewals.length - 6} more…
              </div>
            )}
          </div>
        </div>
      )}

      {/* Three full-width rows, each independently collapsible so the
          dashboard stays scannable as data grows. Order matches the mental
          flow: what's incoming (Backorders) → what's running low (Low stock)
          → what's happened (Recent activity). */}
      <div className="flex flex-col gap-5 mt-6">
        <BackordersWidget
          expanded={expBackorders}
          onToggle={() => setExpBackorders((v) => !v)}
          onSummaryChange={setBackorderSummary}
        />

        <div className="nb-card p-5" data-testid="low-stock-panel">
          <button
            type="button"
            data-testid="low-stock-toggle"
            onClick={() => setExpLowStock((v) => !v)}
            className="w-full flex items-center gap-2 mb-3 text-left"
            aria-expanded={expLowStock}
          >
            {expLowStock ? (
              <ChevronDown size={18} strokeWidth={2.5} />
            ) : (
              <ChevronRight size={18} strokeWidth={2.5} />
            )}
            <AlertTriangle size={20} strokeWidth={2.5} className="text-[#E5263F]" />
            <h2 className="font-display text-xl">Low stock</h2>
            {(stats?.low_stock_count ?? 0) > 0 && (
              <span className="label-eyebrow px-2 py-0.5 rounded-full border border-black/30 bg-[#FFF1F3] text-[#8A1F1F] ml-auto">
                {stats.low_stock_count}
              </span>
            )}
          </button>
          {expLowStock &&
            (stats?.low_stock_items?.length ? (
            <div className="flex flex-col gap-2">
              {(showAllLow
                ? stats.low_stock_items
                : stats.low_stock_items.slice(0, 6)
              ).map((it) => (
                <button
                  key={it.id}
                  type="button"
                  data-testid={`dash-low-${it.id}`}
                  onClick={() =>
                    nav(
                      isManager
                        ? `/orders?focus=${it.id}`
                        : `/take?focus=${it.id}`,
                    )
                  }
                  title={
                    isManager
                      ? "Open reorder draft for this item"
                      : "Find this item on Take stock"
                  }
                  className="flex items-center gap-3 border-2 border-black rounded-xl p-2 text-left hover:bg-[#FFF0F1] hover:ring-2 hover:ring-[#E5263F]/30 transition"
                >
                  <StockThumb item={it} size="h-12 w-12" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base truncate">{it.name}</div>
                    <div className="label-eyebrow">
                      {it.type}
                      {isManager ? " · tap to reorder" : " · tap to view"}
                    </div>
                    <LowStockActionPill
                      openPo={it.open_po}
                      openBackorder={it.open_backorder}
                    />
                  </div>
                  <div className="font-mono-num font-bold text-lg px-2 py-0.5 rounded border-2 border-black bg-[#FFEC8A]">
                    {fmtQty(it.current_qty)}
                  </div>
                </button>
              ))}
              {stats.low_stock_items.length > 6 && (
                <button
                  type="button"
                  data-testid="dash-low-toggle"
                  onClick={() => setShowAllLow((v) => !v)}
                  className="mt-1 nb-btn bg-white px-3 py-2 text-xs font-bold self-start"
                >
                  {showAllLow
                    ? "Show top 6"
                    : `Show all ${stats.low_stock_items.length}`}
                </button>
              )}
            </div>
          ) : (
            <div className="text-[#6B6B6B] text-sm py-6 text-center">
              All good — nothing below threshold.
            </div>
          ))}
        </div>

        <div className="nb-card p-5" data-testid="recent-activity">
          <button
            type="button"
            data-testid="recent-activity-toggle"
            onClick={() => setExpRecent((v) => !v)}
            className="w-full flex items-center gap-2 mb-3 text-left"
            aria-expanded={expRecent}
          >
            {expRecent ? (
              <ChevronDown size={18} strokeWidth={2.5} />
            ) : (
              <ChevronRight size={18} strokeWidth={2.5} />
            )}
            <h2 className="font-display text-xl">Recent activity</h2>
            {(stats?.recent?.length ?? 0) > 0 && (
              <span className="label-eyebrow px-2 py-0.5 rounded-full border border-black/30 bg-[#EAF5FB] text-[#0A3A7A] ml-auto">
                {stats.recent.length}
              </span>
            )}
          </button>
          {expRecent && (
          <div className="flex flex-col gap-2">
            {stats?.recent?.length ? (
              stats.recent.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  data-testid={`recent-row-${m.id}`}
                  onClick={() => openItemHistory(m.stock_id)}
                  className="flex flex-wrap items-center gap-x-3 gap-y-1 border-2 border-black rounded-xl p-3 text-left hover:bg-[#EAF5FB] hover:ring-2 hover:ring-[#228EBF]/30 transition"
                  title="Open item history"
                >
                  <div
                    className="font-display px-2.5 py-0.5 border-2 border-black rounded text-xs tracking-wider"
                    style={{
                      background: m.action === "OUT" ? "#228EBF" : "#5FBE7E",
                      color: "#fff",
                    }}
                  >
                    {m.action}
                  </div>
                  <div className="font-mono-num font-bold text-base">
                    {m.sub_qty && m.sub_unit ? (
                      <>
                        {fmtQty(m.sub_qty)} <span className="text-[#6B6B6B]">{m.sub_unit}</span>
                      </>
                    ) : (
                      <>
                        {fmtQty(m.qty)} <span className="text-[#6B6B6B]">{m.unit}</span>
                      </>
                    )}
                  </div>
                  <div className="font-display text-base truncate min-w-0 flex-1 basis-full sm:basis-auto">
                    {m.stock_name}
                  </div>
                  <div className="label-eyebrow text-[#607087] flex items-center flex-wrap gap-x-1.5 gap-y-1 shrink-0 w-full sm:w-auto sm:whitespace-nowrap">
                    <span>{fmtWhen(m.created_at)}</span>
                    <span>·</span>
                    <span>by {m.user_name}</span>
                    {m.department && (
                      <span data-testid={`recent-dept-${m.id}`}>
                        <DepartmentBadge department={m.department} />
                      </span>
                    )}
                    {m.requester && (
                      <span
                        className="px-1.5 py-0.5 rounded-md bg-[#EAF5FB] text-[#0A3A7A] border border-[#0A3A7A]/40 truncate max-w-[8rem]"
                        title="Requester"
                      >
                        ← {m.requester}
                      </span>
                    )}
                  </div>
                </button>
              ))
            ) : (
              <div className="text-[#6B6B6B] text-sm py-6 text-center">
                No activity yet — go take some stock!
              </div>
            )}
          </div>
          )}
        </div>
      </div>

      {(assetAlerts.warrantySoon.length > 0 || assetAlerts.broken.length > 0) && (
        <div className="nb-card p-5 mt-6" data-testid="dash-assets-alerts">
          <div className="label-eyebrow mb-3">Asset alerts</div>
          <div className="grid gap-3 sm:grid-cols-2">
            {assetAlerts.warrantySoon.length > 0 && (
              <button
                type="button"
                data-testid="dash-warranty-soon"
                onClick={() => nav("/assets")}
                className="text-left border-2 border-black rounded-xl p-3 hover:bg-[#FFF7DB] transition"
              >
                <div className="font-display text-lg">
                  {assetAlerts.warrantySoon.length} warrant
                  {assetAlerts.warrantySoon.length === 1 ? "y" : "ies"} expiring &lt; 90 days
                </div>
                <div className="label-eyebrow mt-1 text-[#607087]">
                  {assetAlerts.warrantySoon.slice(0, 3).map((a) => a.name).join(" · ")}
                </div>
              </button>
            )}
            {assetAlerts.broken.length > 0 && (
              <button
                type="button"
                data-testid="dash-broken-assets"
                onClick={() => nav("/assets")}
                className="text-left border-2 border-black rounded-xl p-3 hover:bg-[#FCE4E4] transition"
              >
                <div className="font-display text-lg">
                  {assetAlerts.broken.length} broken /
                  awaiting confirmation
                </div>
                <div className="label-eyebrow mt-1 text-[#607087]">
                  {assetAlerts.broken.slice(0, 3).map((a) => a.name).join(" · ")}
                </div>
              </button>
            )}
          </div>
        </div>
      )}

      {(machineAlerts.due_soon > 0 ||
        machineAlerts.overdue > 0 ||
        machineAlerts.broken > 0) && (
        <div className="nb-card p-5 mt-6" data-testid="dash-machinery-alerts">
          <div className="label-eyebrow mb-3">Machinery alerts</div>
          <div className="grid gap-3 sm:grid-cols-3">
            {machineAlerts.overdue > 0 && (
              <button
                type="button"
                data-testid="dash-machinery-overdue"
                onClick={() => nav("/machinery")}
                className="text-left border-2 border-black rounded-xl p-3 hover:bg-[#FCE4E4] transition"
              >
                <div className="font-display text-lg">
                  {machineAlerts.overdue} overdue service
                  {machineAlerts.overdue === 1 ? "" : "s"}
                </div>
                <div className="label-eyebrow mt-1 text-[#607087]">
                  {(machineAlerts.overdue_items || [])
                    .slice(0, 3)
                    .map((a) => a.name)
                    .join(" · ")}
                </div>
              </button>
            )}
            {machineAlerts.due_soon > 0 && (
              <button
                type="button"
                data-testid="dash-machinery-due-soon"
                onClick={() => nav("/machinery")}
                className="text-left border-2 border-black rounded-xl p-3 hover:bg-[#FFF7DB] transition"
              >
                <div className="font-display text-lg">
                  {machineAlerts.due_soon} service
                  {machineAlerts.due_soon === 1 ? "" : "s"} due ≤ 14d
                </div>
                <div className="label-eyebrow mt-1 text-[#607087]">
                  {(machineAlerts.due_soon_items || [])
                    .slice(0, 3)
                    .map((a) => a.name)
                    .join(" · ")}
                </div>
              </button>
            )}
            {machineAlerts.broken > 0 && (
              <button
                type="button"
                data-testid="dash-machinery-broken"
                onClick={() => nav("/machinery")}
                className="text-left border-2 border-black rounded-xl p-3 hover:bg-[#FCE4E4] transition"
              >
                <div className="font-display text-lg">
                  {machineAlerts.broken} out of service
                </div>
                <div className="label-eyebrow mt-1 text-[#607087]">
                  {(machineAlerts.broken_items || [])
                    .slice(0, 3)
                    .map((a) => a.name)
                    .join(" · ")}
                </div>
              </button>
            )}
          </div>
        </div>
      )}

      {isManager && <AdminListNudgesWidget />}

      <ItemHistoryDialog
        open={!!historyFor}
        item={historyFor}
        onClose={() => setHistoryFor(null)}
      />
    </div>
  );
};

export default Dashboard;
